import React, { useEffect } from "react";
import {
  $Button,
  $TextArea,
  $Popover,
} from "us.common/components";
import { Formik, Form } from "formik";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "us.helper/types";
import { uniQActions } from "us.common/actions/UniQ";
import { useTranslation } from "react-i18next";
import { matchPath, useLocation } from "react-router-dom";
import { hasCaseSummary } from "../../functions";
import { UniQChatMessage } from "us.common/interfaces";
import { useAuth } from "us.helper/hooks/useAuth";
import "./SettingsPopover.scss";

/**
 * SettingsPopover Component
 *
 * A popover component for editing the default system prompt for the UniQ assistant.
 * Integrated with Redux for state management and Formik for form handling.
 *
 * Features:
 * - Popover visibility controlled by Redux state
 * - Formik form validation and submission
 * - Multi-line textarea for prompt editing
 * - Save and Cancel actions
 *
 * @component
 * @returns {JSX.Element} The rendered popover component
 */
interface SettingsPopoverProps extends PropsFromRedux {
  children?: React.ReactNode;
  messages: UniQChatMessage[]
}

const SettingsPopover: React.FC<SettingsPopoverProps> = (
  props,
): JSX.Element => {
  const { t } = useTranslation("US.COMMON");
  const location = useLocation();
  const { account } = useAuth();

  const { defaultPrompt, messages, openSettings, savePrompt, getDefaultPrompt, children } =
    props;
  const { isVisible, isFetching, isSaving, data } = defaultPrompt;
  const pathname = location.pathname;
  const { params }: any = matchPath(
    {
      path: "/:type/:id?/:module?",
      end: false,
      caseSensitive: false,
    },
    pathname,
  ) ?? { params: {} };
  const { id: caseNo } = params ?? {};

  useEffect(() => {
    hasCaseSummary(messages, caseNo) &&
      data?.executedUserPrompt == "" &&
      getDefaultPrompt &&
      getDefaultPrompt({ caseNo });
  }, [caseNo]);

  return (
    <Formik
      initialValues={{ prompt: data?.executedUserPrompt || "" }}
      enableReinitialize
      onSubmit={(values) => {
        savePrompt && savePrompt({ userName: account?.username, userAIPrompt:values.prompt  });
      }}
    >
      {({ values, setFieldValue, handleSubmit, resetForm }) => (
        <$Popover
          title={t("US.COMMON:UNIQ.EDIT_DEFAULT_PROMPT_FOR_CASE_SUMMARY")}
          open={isVisible}
          trigger="click"
          placement="bottomRight"
          onOpenChange={(visible: boolean) => {
            if (!visible) {
              resetForm();
              openSettings && openSettings({});
            }
          }}
          content={
            <Form>
              <div className="settings-popover-content mt-3">
                <$TextArea
                  rows={8}
                  name="prompt"
                  value={values.prompt}
                  disabled={isSaving || isFetching}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setFieldValue("prompt", e.target.value)
                  }
                  placeholder={t("US.COMMON:UNIQ.ENTER_DEFAULT_PROMPT")}
                  style={{ resize: "vertical" }}
                />
                <div className="settings-popover-footer">
                  <$Button
                    size="small"
                    onClick={() => {
                      resetForm();
                      openSettings && openSettings({});
                    }}
                  >
                    {t("US.COMMON:COMMON.CANCEL")}
                  </$Button>
                  <$Button
                    type="primary"
                    size="small"
                    disabled={isSaving || isFetching}
                    onClick={() => handleSubmit()}
                  >
                    {t("US.COMMON:COMMON.SAVE")}
                  </$Button>
                </div>
              </div>
            </Form>
          }
        >
          {children ?? <span className="settings-popover-anchor" />}
        </$Popover>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { uniQ } = state;

  return {
    defaultPrompt: uniQ.defaultPrompt
  };
};

const mapDispatchToProps = {
  getDefaultPrompt: uniQActions.defaultPromptData.get,
  openSettings: uniQActions.defaultPrompt.handlePopover,
  savePrompt: uniQActions.defaultPrompt.save,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(SettingsPopover);
